import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton } from '@mui/material';
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
    type ReactNode,
} from 'react';

export type HorizontalScrollRowHandle = {
    scrollBy: (direction: -1 | 1) => void;
    updateScrollEdges: () => void;
    readonly scrollElement: HTMLDivElement | null;
};

type HorizontalScrollRowProps = {
    children: ReactNode;
    ariaLabel: string;
    borderBottom?: boolean;
    leftAriaLabel?: string;
    rightAriaLabel?: string;
    role?: string;
};

export const HorizontalScrollRow = forwardRef<
    HorizontalScrollRowHandle,
    HorizontalScrollRowProps
>(function HorizontalScrollRow(
    {
        children,
        ariaLabel,
        borderBottom = false,
        leftAriaLabel = 'Scroll left',
        rightAriaLabel = 'Scroll right',
        role = 'region',
    },
    ref
) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const updateScrollEdges = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const { scrollLeft, scrollWidth, clientWidth } = el;
        const epsilon = 2;
        setCanScrollLeft(scrollLeft > epsilon);
        setCanScrollRight(scrollLeft + clientWidth < scrollWidth - epsilon);
    }, []);

    const scrollBy = useCallback((direction: -1 | 1) => {
        const el = scrollRef.current;
        if (!el) return;
        const step = Math.max(220, Math.floor(el.clientWidth * 0.65));
        el.scrollBy({ left: step * direction, behavior: 'smooth' });
    }, []);

    useImperativeHandle(
        ref,
        () => ({
            scrollBy,
            updateScrollEdges,
            get scrollElement() {
                return scrollRef.current;
            },
        }),
        [scrollBy, updateScrollEdges]
    );

    useLayoutEffect(() => {
        updateScrollEdges();
    }, [children, updateScrollEdges]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => updateScrollEdges());
        ro.observe(el);
        el.addEventListener('scroll', updateScrollEdges, { passive: true });
        return () => {
            ro.disconnect();
            el.removeEventListener('scroll', updateScrollEdges);
        };
    }, [updateScrollEdges]);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'flex-end',
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                ...(borderBottom && {
                    borderBottom: 1,
                    borderColor: 'divider',
                }),
            }}
        >
            <IconButton
                size="small"
                aria-label={leftAriaLabel}
                disabled={!canScrollLeft}
                onClick={() => scrollBy(-1)}
                sx={{ flexShrink: 0, alignSelf: 'center', mb: 0.5 }}
            >
                <ChevronLeftIcon />
            </IconButton>
            <Box
                ref={scrollRef}
                role={role}
                aria-label={ariaLabel}
                sx={theme => ({
                    display: 'flex',
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    alignItems: 'flex-end',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    flex: 1,
                    minWidth: 0,
                    maxWidth: '100%',
                    WebkitOverflowScrolling: 'touch',
                    scrollbarGutter: 'stable',
                    scrollbarWidth: 'thin',
                    scrollbarColor: `${theme.palette.action.hover} ${theme.palette.divider}`,
                    '&::-webkit-scrollbar': {
                        height: 8,
                    },
                    '&::-webkit-scrollbar-track': {
                        backgroundColor: theme.palette.divider,
                    },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.action.hover,
                        borderRadius: 4,
                    },
                })}
            >
                {children}
            </Box>
            <IconButton
                size="small"
                aria-label={rightAriaLabel}
                disabled={!canScrollRight}
                onClick={() => scrollBy(1)}
                sx={{ flexShrink: 0, alignSelf: 'center', mb: 0.5 }}
            >
                <ChevronRightIcon />
            </IconButton>
        </Box>
    );
});
